import {
    VisitRequestTableRowFragment,
    VisitTableRowFragment,
} from '@/components/bulkQueue/tables/queries.gql.types';
import { Store, createStore } from '@/utils/store';
import { useCallback, useRef, useState } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export type BulkState = 'loading' | 'idle' | 'submitting';

export type Entity = VisitTableRowFragment | VisitRequestTableRowFragment;

export const useBulkState = (loading: boolean, submitting: boolean): BulkState => {
    if (submitting) {
        return 'submitting';
    }
    return loading ? 'loading' : 'idle';
};

//A stable, reactive key of the entity ids whose selection-store value is a pick
//(per `isPicked`). Selection stores mutate outside React and don't re-render
//consumers on their own, so use this as a `useMemo` dependency to recompute
//derived sets (e.g. selectable ids) when the user picks or clears.
//Returns a sorted, comma joined string.
export const useSelectedEntityIdsKey = <T>(
    store: Pick<Store<T>, 'subscribeToAll' | 'getAll'>,
    isPicked: (value: T) => boolean,
): string => {
    const subscribe = useCallback((cb: () => void) => store.subscribeToAll(cb), [store]);
    const getSnapshot = useCallback(() => {
        const ids: string[] = [];
        store.getAll().forEach((value, entityId) => {
            if (isPicked(value)) {
                ids.push(entityId);
            }
        });
        return ids.sort().join(',');
    }, [store, isPicked]);
    return useSyncExternalStore(subscribe, getSnapshot);
};

export const useBulkSelectionStore = (): [
    store: Store<boolean>,
    setValue: (rowId: string, isSelected: boolean | null) => void,
    selectedCount: number,
    resetSelection: () => void,
    pruneToEntityIds: (entityIds: Set<string>) => void,
] => {
    const selectionStoreRef = useRef(createStore<boolean>());
    const selectionStore = selectionStoreRef.current;
    const [selectedCount, setSelectedCount] = useState(0);

    const recount = useCallback(() => {
        let count = 0;
        selectionStore.getAll().forEach((v) => {
            if (v) {
                count++;
            }
        });
        setSelectedCount(count);
    }, [selectionStore]);

    const setValue = useCallback(
        (rowId: string, isSelected: boolean | null) => {
            if (isSelected === null) {
                selectionStore.setValue(rowId, !selectionStore.getSnapshot(rowId));
            } else {
                selectionStore.setValue(rowId, isSelected);
            }
            recount();
        },
        [selectionStore, recount],
    );

    const resetSelection = useCallback(() => {
        selectionStore.setAll(new Map());
        setSelectedCount(0);
    }, [selectionStore]);

    const pruneToEntityIds = useCallback(
        (entityIds: Set<string>) => {
            const all = selectionStore.getAll();
            const next = new Map<string, boolean>();
            let count = 0;
            all.forEach((v, id) => {
                if (entityIds.has(id)) {
                    next.set(id, v);
                    if (v) {
                        count++;
                    }
                }
            });
            if (next.size !== all.size) {
                selectionStore.setAll(next);
                setSelectedCount(count);
            }
        },
        [selectionStore],
    );

    return [selectionStore, setValue, selectedCount, resetSelection, pruneToEntityIds];
};

export const getOrCreateEntityMap = <TValue>(
    map: Map<string, Map<string, TValue>>,
    entityId: string,
): Map<string, TValue> => {
    const existing = map.get(entityId);
    if (existing) {
        return existing;
    }
    const next = new Map<string, TValue>();
    map.set(entityId, next);
    return next;
};
